import Vue from 'vue';
import App from './App.vue';
import router from './router';
import i18n from './lang/lang';
import AppConfig from '@/configLoader';
import VueGtag from 'vue-gtag';
import { applyTheme, resolveTheme } from '@/services/ThemeService';
import VueCookies from 'vue-cookies';
import Axios from 'axios';
import Buefy from 'buefy';
import '@fortawesome/fontawesome-free/css/solid.css';
import '@fortawesome/fontawesome-free/css/regular.css';
import '@fortawesome/fontawesome-free/css/fontawesome.css';
import { Workbox } from 'workbox-window';
// In case the script in main didn't run or storage changed
applyTheme(resolveTheme());
Vue.config.productionTip = false;
Vue.use(VueCookies);
Vue.use(Buefy, {
    defaultIconPack: 'fas'
});
import VCalendar from 'v-calendar';
Vue.use(VCalendar);
Vue.prototype.$axios = Axios;
Vue.prototype.$features = Features;
Vue.prototype.$featuresManager = FeaturesManager.getInstance();
Vue.prototype.$adminPermissions = AdminPermissions;
Vue.prototype.$customerPermissions = CustomerPermissions;
Vue.prototype.$userCan = LoggedUserManager.checkPermissions;
export const globalStore = new Vue({
    data: {}
});
Vue.prototype.$globals = globalStore;
Vue.directive('visible', function (el, binding) {
    el.style.visibility = !!binding.value ? 'visible' : 'hidden';
});
import vClickOutside from 'v-click-outside';
Vue.use(vClickOutside);
//Leaflet map and icons importing
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
Vue.component('l-map', LMap);
Vue.component('l-tile-layer', LTileLayer);
Vue.component('l-marker', LMarker);
Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});
import { Features } from './entities/tiers/Features';
import { FeaturesManager } from './entities/tiers/FeaturesManager';
import VueUtilities from './services/VueUtilities';
import { AdminPermissions } from './services/permissions/AdminPermissions';
import { CustomerPermissions } from './services/permissions/CustomerPermissions';
import LoggedUserManager from './services/LoggedUserManager';
delete Icon.Default.prototype._getIconUrl;
const messagesLocale = {
    sk: require('@/lang/sk.json'),
    en: require('@/lang/en.json'),
    cs: require('@/lang/cs.json')
};
import { ValidationProvider, ValidationObserver } from 'vee-validate';
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
import { extend } from 'vee-validate';
import { required, email, min, max, numeric, min_value, max_value, integer, length } from 'vee-validate/dist/rules';
import { initFaro, logError } from './services/logging/FaroLogger';
import { TelemetryErrorTypes } from './services/logging/TelemetryEvents';
extend('email', email);
extend('required', required);
extend('min', min);
extend('max', max);
extend('numeric', numeric);
extend('min_value', min_value);
extend('max_value', max_value);
extend('integer', integer);
extend('length', length);
extend('passwordSame', {
    params: ['target'],
    validate(value, { target }) {
        return value === target;
    },
    message: i18n.t('validation.pswdSame')
});
extend('passwordContains', {
    validate: (value) => {
        return value.match(/[A-Z]/g) !== null && value.match(/[0-9]/g) !== null && value.match(/[a-z]/g) !== null;
    },
    message: i18n.t('validation.pswdContains')
});
extend('phoneE164', {
    validate: (value) => {
        return value.match(/^\+[1-9]\d{6,14}$/) != null;
    },
    message: i18n.t('validation.phoneE164')
});
extend('deviceRegistrationKey', {
    validate: (value) => {
        return value.match('^[0-9A-Z-]*$') != null;
    },
    message: i18n.t('validation.deviceRegKey')
});
extend('decimalNumber', {
    validate: (value) => {
        if (value === null || value === undefined || value === '')
            return true;
        const str = String(value); // prevent TypeError
        return str.match(/^-?(0|[1-9]\d*)(\,\d{1,2})?$/) !== null;
    },
    message: () => i18n.t('validation.decimalNumber')
});
extend('decimalRange', {
    params: ['min', 'max'],
    validate: (value, params) => {
        if (!value)
            return true;
        const normalized = parseFloat(value.replace(',', '.'));
        const min = params['min'] !== undefined ? Number(params['min']) : undefined;
        const max = params['max'] !== undefined ? Number(params['max']) : undefined;
        if ((min !== undefined && normalized < min) || (max !== undefined && normalized > max))
            return false;
        return true;
    },
    message: (field, params) => {
        return i18n.tc('validation.decimalRange', 1, {
            min: params['min'],
            max: params['max'],
            field
        });
    }
});
//PWA
// Reload the page when the new service worker takes control.
// The refreshing flag prevents a double-reload if multiple listeners fire.
let refreshing = false;
if (navigator.serviceWorker)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
            refreshing = true;
            window.location.reload();
        }
    });
let wb = null;
if ('serviceWorker' in navigator) {
    wb = new Workbox('/service-worker.js');
    // Automatically apply the waiting service worker — no user confirmation needed.
    // Set the flag here (before reload) rather than in 'activated', which can race
    // with the 'controllerchange' reload and fire too late.
    wb.addEventListener('waiting', () => {
        sessionStorage.setItem('sw-updated', '1');
        wb.messageSkipWaiting();
    });
    // Register immediately so no 'waiting' events are missed.
    // Polling interval is configured after AppConfig loads below.
    wb.register().catch((error) => {
        logError(error, TelemetryErrorTypes.NETWORK, 'Service worker registration failed');
    });
}
Vue.component('default-layout', () => import('./views/layouts/Default.vue'));
Vue.component('with-sidebar-layout', () => import('./views/layouts/WithSidebar.vue'));
Vue.component('landing-page-layout', () => import('./views/layouts/LandingPage.vue'));
Vue.component('organization-layout', () => import('./views/layouts/WithOrganizationSideBar.vue'));
Vue.component('admin-layout', () => import('./views/layouts/WithAdminSidebar.vue'));
Vue.component('public-layout', () => import('./views/layouts/Public.vue'));
AppConfig.loadConfig().then(() => {
    if (AppConfig.getConfig().google_analytics.allowed == true) {
        Vue.use(VueGtag, {
            config: { id: AppConfig.getConfig().google_analytics.id },
            bootstrap: false,
            enabled: false
        }, router);
    }
    //initialize faro logging
    initFaro();
    // Show the "app updated" toast after an automatic SW update reload.
    if (sessionStorage.getItem('sw-updated')) {
        sessionStorage.removeItem('sw-updated');
        VueUtilities.openSuccessToast(i18n.t('app_updated'));
    }
    // Proactive update checks for users who keep the same page open indefinitely.
    // Interval and visibility trigger are set up here so the frequency can be
    // driven by AppConfig rather than hardcoded.
    if (wb) {
        const checkForUpdate = () => wb.update().catch((error) => {
            logError(error, TelemetryErrorTypes.NETWORK, 'Service worker update check failed');
        });
        const intervalMs = AppConfig.getConfig().attributes.sw_update_interval_ms;
        setInterval(checkForUpdate, intervalMs);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible')
                checkForUpdate();
        });
    }
    const app = new Vue({
        router,
        i18n,
        render: (h) => h(App)
    });
    router.onReady(() => {
        app.$mount('#app');
    });
});
